import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import { staffApi, StaffSchedule, StaffScheduleException } from '../services/staffService';
import { EmptyState, StatusPill, useConfirm } from './ui';
import { tokens } from '../theme';
import { formatDate } from '../utils/format';

const DAY_NAMES = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];

// Dane trzymaja tydzien od niedzieli (jak backend), ale w salonie tydzien czyta sie od poniedzialku.
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const todayKey = () => new Date().toISOString().slice(0, 10);

const weekdayName = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : DAY_NAMES[d.getDay()];
};

// Wspolny edytor grafiku pracy pracownika.
// Uzywany w dwoch miejscach: na stronie „Pracownicy" (gdzie grafiki sa juz
// wczytane i przekazywane przez `currentSchedules`) oraz w kalendarzu
// (gdzie komponent dociaga grafik sam, po kliknieciu w naglowek pracownika).
const StaffScheduleDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  staffId: string;
  staffName: string;
  currentSchedules?: StaffSchedule[];
}> = ({ open, onClose, onSave, staffId, staffName, currentSchedules }) => {
  const confirm = useConfirm();
  const [tab, setTab] = useState(0);
  const [schedules, setSchedules] = useState<StaffSchedule[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Wyjatki zapisuja sie od razu, po jednym — inaczej niz grafik tygodniowy,
  // ktory idzie do bazy calym tygodniem po kliknieciu "Zapisz".
  const [exceptions, setExceptions] = useState<StaffScheduleException[]>([]);
  const [exLoading, setExLoading] = useState(false);
  const [exSaving, setExSaving] = useState(false);
  const [exError, setExError] = useState('');
  const [form, setForm] = useState({
    date: '', isOff: true, startTime: '09:00', endTime: '17:00', reason: '',
  });

  useEffect(() => {
    if (!open) return;

    // Grafik zawsze pokazujemy jako pelny tydzien — dni bez wpisu to dni wolne.
    const toFullWeek = (source: StaffSchedule[]): StaffSchedule[] => {
      const full: StaffSchedule[] = [];
      for (let d = 0; d <= 6; d++) {
        const existing = source.find((s) => s.dayOfWeek === d);
        full.push(
          existing
            ? { ...existing }
            : { dayOfWeek: d, startTime: '09:00', endTime: '17:00', isWorking: false },
        );
      }
      return full;
    };

    setError('');

    if (currentSchedules) {
      setSchedules(toFullWeek(currentSchedules));
      return;
    }

    let cancelled = false;
    setLoading(true);
    staffApi
      .getSchedule(staffId)
      .then((data) => {
        if (!cancelled) setSchedules(toFullWeek(data || []));
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(err?.response?.data?.error || 'Nie udało się wczytać grafiku. Zamknij okno i spróbuj ponownie.');
          setSchedules(toFullWeek([]));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentSchedules, open, staffId]);

  // Wyjatki z przeszlosci nie sa juz nikomu potrzebne — lista zaczyna sie od dzis.
  const loadExceptions = useCallback(async () => {
    setExLoading(true);
    setExError('');
    try {
      setExceptions(await staffApi.getScheduleExceptions(staffId, { from: todayKey() }));
    } catch (err: any) {
      setExError(err?.response?.data?.error || 'Nie udało się wczytać wyjątków');
    } finally {
      setExLoading(false);
    }
  }, [staffId]);

  useEffect(() => {
    if (open && tab === 1) loadExceptions();
  }, [open, tab, loadExceptions]);

  // Powrot do zakladki tygodniowej przy kazdym otwarciu — to ona jest
  // punktem wyjscia, wyjatki sa dopiskiem.
  useEffect(() => {
    if (open) setTab(0);
  }, [open]);

  const handleAddException = async () => {
    if (!form.date) return;
    setExSaving(true);
    setExError('');
    try {
      await staffApi.upsertScheduleException(staffId, {
        date: form.date,
        isOff: form.isOff,
        ...(form.isOff ? {} : { startTime: form.startTime, endTime: form.endTime }),
        ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
      });
      setForm({ date: '', isOff: true, startTime: '09:00', endTime: '17:00', reason: '' });
      await loadExceptions();
      onSave();
    } catch (err: any) {
      setExError(err?.response?.data?.error || 'Nie udało się zapisać wyjątku');
    } finally {
      setExSaving(false);
    }
  };

  const handleDeleteException = async (ex: StaffScheduleException) => {
    const ok = await confirm({
      title: 'Usunąć wyjątek w grafiku?',
      details: (
        <>
          <strong>{staffName}</strong> · {weekdayName(ex.date)}, {formatDate(ex.date)}
          {ex.reason ? ` · ${ex.reason}` : ''}
        </>
      ),
      description: 'Tego dnia znów będzie obowiązywał grafik tygodniowy.',
      confirmLabel: 'Usuń wyjątek',
      tone: 'danger',
    });
    if (!ok) return;
    setExError('');
    try {
      await staffApi.deleteScheduleException(staffId, ex.id);
      setExceptions((prev) => prev.filter((e) => e.id !== ex.id));
      onSave();
    } catch (err: any) {
      setExError(err?.response?.data?.error || 'Nie udało się usunąć wyjątku');
    }
  };

  const updateDay = (dayOfWeek: number, field: string, value: any) => {
    setSchedules((prev) =>
      prev.map((s) => (s.dayOfWeek === dayOfWeek ? { ...s, [field]: value } : s)),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await staffApi.setSchedule(
        staffId,
        schedules.map((s) => ({
          dayOfWeek: s.dayOfWeek,
          startTime: s.startTime,
          endTime: s.endTime,
          isWorking: s.isWorking,
        })),
      );
      onSave();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nie udało się zapisać grafiku. Sprawdź godziny i spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  const orderedSchedules = DISPLAY_ORDER
    .map((dow) => schedules.find((s) => s.dayOfWeek === dow))
    .filter((s): s is StaffSchedule => Boolean(s));
  const workingDays = schedules.filter((s) => s.isWorking).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="staff-schedule-title">
      <DialogTitle id="staff-schedule-title" sx={{ pb: 1 }}>
        Grafik pracy
        <Typography component="span" sx={{ display: 'block', fontSize: '0.9375rem', fontWeight: 500, color: 'text.secondary', mt: 0.25 }}>
          {staffName}
        </Typography>
      </DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ px: { xs: 2.25, sm: 3 }, borderBottom: `1px solid ${tokens.line}` }}
      >
        <Tab label="Tydzień" />
        <Tab label="Wyjątki" />
      </Tabs>
      <DialogContent sx={{ pt: 1.5 }}>
        {tab === 0 && (
          <>
            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
            <Typography variant="body2" sx={{ color: tokens.inkSoft, mb: 1 }}>
              Stały tydzień pracy. Dni z wyłączonym przełącznikiem są wolne
              {!loading && ` — pracuje ${workingDays} z 7 dni`}.
            </Typography>
            {loading ? (
              <Stack gap={1.25} sx={{ py: 1 }} aria-busy="true" aria-label="Wczytywanie grafiku">
                {DISPLAY_ORDER.map((d) => (
                  <Skeleton key={d} variant="rounded" height={46} />
                ))}
              </Stack>
            ) : (
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {orderedSchedules.map((s) => {
                  const day = DAY_NAMES[s.dayOfWeek];
                  const invalid = s.isWorking && s.startTime >= s.endTime;
                  return (
                    <Box
                      component="li"
                      key={s.dayOfWeek}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 1, sm: 2 },
                        py: 1.25,
                        minHeight: 70,
                        borderBottom: `1px solid ${tokens.line}`,
                        '&:last-of-type': { borderBottom: 0 },
                      }}
                    >
                      <Stack direction="row" alignItems="center" gap={1.5} sx={{ width: { sm: 200 }, flexShrink: 0, minWidth: 0 }}>
                        <Switch
                          checked={s.isWorking}
                          onChange={(e) => updateDay(s.dayOfWeek, 'isWorking', e.target.checked)}
                          inputProps={{ 'aria-label': `${day} — dzień pracy` }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 650, fontSize: '0.9375rem', lineHeight: 1.35 }}>{day}</Typography>
                          <Typography variant="caption" component="div" sx={{ color: s.isWorking ? tokens.success : tokens.muted }}>
                            {s.isWorking ? 'Pracuje' : 'Wolne'}
                          </Typography>
                        </Box>
                      </Stack>
                      {s.isWorking && (
                        <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0, pl: { xs: 7.25, sm: 0 } }}>
                          <TextField
                            type="time"
                            value={s.startTime}
                            onChange={(e) => updateDay(s.dayOfWeek, 'startTime', e.target.value)}
                            error={invalid}
                            sx={{ flex: 1, minWidth: 0 }}
                            inputProps={{ step: 900, 'aria-label': `${day}: od godziny` }}
                          />
                          <Typography aria-hidden sx={{ color: tokens.muted }}>–</Typography>
                          <TextField
                            type="time"
                            value={s.endTime}
                            onChange={(e) => updateDay(s.dayOfWeek, 'endTime', e.target.value)}
                            error={invalid}
                            sx={{ flex: 1, minWidth: 0 }}
                            inputProps={{ step: 900, 'aria-label': `${day}: do godziny` }}
                          />
                        </Stack>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}

        {tab === 1 && (
          <>
            <Typography variant="body2" sx={{ color: tokens.inkSoft, mb: 1.5 }}>
              Odstępstwa od grafiku tygodniowego w konkretnym dniu — wolne albo inne
              godziny niż zwykle. Wyjątek zapisuje się od razu po dodaniu.
            </Typography>

            {exError && <Alert severity="error" sx={{ mb: 1.5 }}>{exError}</Alert>}

            <Paper variant="outlined" sx={{ p: { xs: 1.75, sm: 2 }, mb: 2, bgcolor: tokens.surface }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', mb: 1.5 }}>Nowy wyjątek</Typography>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                <TextField
                  type="date"
                  label="Dzień"
                  InputLabelProps={{ shrink: true }}
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  fullWidth
                  sx={{ minWidth: 170 }}
                />
                <Box sx={{ display: 'flex', alignItems: 'flex-end', minHeight: { sm: 72 } }}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={!form.isOff}
                        onChange={(e) => setForm({ ...form, isOff: !e.target.checked })}
                      />
                    )}
                    label={form.isOff ? 'Wolne cały dzień' : 'Pracuje w innych godzinach'}
                    sx={{ minHeight: 46 }}
                  />
                </Box>
                {!form.isOff && (
                  <Box sx={{ gridColumn: '1 / -1', display: 'grid', gap: 2, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                    <TextField
                      type="time"
                      label="Od"
                      InputLabelProps={{ shrink: true }}
                      value={form.startTime}
                      onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                      inputProps={{ step: 900 }}
                      fullWidth
                    />
                    <TextField
                      type="time"
                      label="Do"
                      InputLabelProps={{ shrink: true }}
                      value={form.endTime}
                      onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                      inputProps={{ step: 900 }}
                      fullWidth
                    />
                  </Box>
                )}
                <TextField
                  fullWidth
                  label="Powód (opcjonalnie)"
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="np. urlop, szkolenie"
                  helperText="Widzi go tylko zespół salonu."
                  sx={{ gridColumn: '1 / -1' }}
                />
              </Box>
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleAddException}
                  disabled={!form.date || exSaving}
                  startIcon={exSaving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Dodaj wyjątek
                </Button>
              </Stack>
            </Paper>

            <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', mb: 0.5 }}>Zaplanowane wyjątki</Typography>
            {exLoading ? (
              <Stack gap={1} sx={{ py: 1 }} aria-busy="true" aria-label="Wczytywanie wyjątków">
                <Skeleton variant="rounded" height={52} />
                <Skeleton variant="rounded" height={52} />
              </Stack>
            ) : exceptions.length === 0 ? (
              <EmptyState
                compact
                icon={<EventAvailableOutlinedIcon />}
                title="Brak wyjątków"
                description="Obowiązuje grafik tygodniowy."
              />
            ) : (
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {exceptions.map((ex) => (
                  <Stack
                    component="li"
                    key={ex.id}
                    direction="row"
                    alignItems="center"
                    gap={1.5}
                    sx={{ py: 1.25, borderBottom: `1px solid ${tokens.line}`, '&:last-of-type': { borderBottom: 0 } }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 650, fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums' }}>
                        {weekdayName(ex.date)}, {formatDate(ex.date)}
                      </Typography>
                      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                        {ex.isOff ? (
                          <StatusPill label="Wolne" tone="neutral" size="small" />
                        ) : (
                          <StatusPill label={`Pracuje ${ex.startTime}–${ex.endTime}`} tone="plum" size="small" />
                        )}
                        {ex.reason && (
                          <Typography variant="caption" sx={{ color: tokens.muted, minWidth: 0, wordBreak: 'break-word' }}>
                            {ex.reason}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                    <Tooltip title="Usuń wyjątek">
                      <IconButton onClick={() => handleDeleteException(ex)} aria-label="Usuń wyjątek">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: `1px solid ${tokens.line}`, pt: 1.5 }}>
        <Button variant="text" onClick={onClose}>{tab === 0 ? 'Anuluj' : 'Zamknij'}</Button>
        {/* Wyjatki zapisuja sie od razu przy dodaniu, wiec przycisk zapisu
            dotyczy wylacznie grafiku tygodniowego. */}
        {tab === 0 && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || loading}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'Zapisywanie…' : 'Zapisz grafik'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StaffScheduleDialog;
